import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { RomaniaMap } from "@/components/site/RomaniaMap";
import { toast } from "sonner";
import {
  Phone, Mail, ExternalLink, Search, MapPin, Shield, Gavel, Users,
  Building2, PhoneCall, Scale, AlertTriangle, ChevronDown, ChevronUp,
  Loader2, Info, Copy, Check, FileText, PlusCircle, X, Send, Sparkles,
  Share2, MessageCircle, Smartphone,
} from "lucide-react";

const TYPE_META = {
  child_protection: { label: "Protecția Copilului", icon: Shield, color: "#D4A63D" },
  police:           { label: "Poliție",              icon: Users,     color: "#5FA0E8" },
  court:            { label: "Instanțe (judecătorie / tribunal)", icon: Gavel, color: "#9C8FE0" },
  prosecutor:       { label: "Parchet",              icon: Scale,     color: "#E89BB8" },
  ombudsman:        { label: "Avocatul Poporului",   icon: Building2, color: "#3BA76D" },
  hotline:          { label: "Linie de urgență",     icon: PhoneCall, color: "#C83A3A" },
  social_services:  { label: "Servicii sociale",     icon: Users,     color: "#6C7A9C" },
  other:            { label: "Altele",               icon: Building2, color: "#A0AABF" },
};
const typeMeta = (t) => TYPE_META[t] || TYPE_META.other;

const TYPE_FILTERS = [
  { v: "toate", l: "Toate" },
  { v: "child_protection", l: "Protecția Copilului" },
  { v: "police", l: "Poliție" },
  { v: "court", l: "Instanțe" },
  { v: "prosecutor", l: "Parchet" },
  { v: "hotline", l: "Linii urgență" },
  { v: "ombudsman", l: "Avocatul Poporului" },
  { v: "social_services", l: "Servicii sociale" },
];

const InstitutionCard = ({ it, onCorrect }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(null);
  const meta = typeMeta(it.type);
  const Icon = meta.icon;
  const isUrgent = it.urgent && it.urgent.toLowerCase().startsWith("da");
  const phones = (it.phone || "").split(";").map(p => p.trim()).filter(Boolean);

  const shareText = useMemo(() => {
    const parts = [
      `📍 ${it.name} — ${it.county}${it.city && it.city !== it.county ? `, ${it.city}` : ""}`,
      it.category ? `Categorie: ${it.category}` : null,
      it.address ? `Adresă: ${it.address}` : null,
      phones.length ? `Telefon: ${phones.join(" · ")}` : null,
      it.email ? `E-mail: ${it.email}` : null,
      it.website ? `Site: ${it.website.split(/\s+/)[0]}` : null,
      "",
      `Registru complet: ${typeof window !== "undefined" ? window.location.origin : "https://tatisisofia.ro"}/institutii`,
    ].filter(Boolean);
    return parts.join("\n");
  }, [it, phones]);

  const shareViaWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };
  const shareViaSMS = () => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = `sms:?body=${encodeURIComponent(shareText)}`;
    } else {
      navigator.clipboard.writeText(shareText).catch(() => {});
      toast.success("SMS-ul funcționează pe telefon. Am copiat textul în clipboard.");
    }
  };
  const shareViaCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied("share");
      toast.success("Contact copiat în clipboard");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Nu am putut copia. Încearcă manual.");
    }
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: it.name, text: shareText });
      } catch { /* user cancelled */ }
    } else {
      shareViaCopy();
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* silent */ }
  };

  return (
    <div
      data-testid={`inst-card-${it.inst_id}`}
      className="rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition"
    >
      <div className="p-5 flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-md grid place-items-center shrink-0"
          style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}40` }}
        >
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded"
              style={{ background: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}
            >
              {it.category || meta.label}
            </span>
            {it.level && (
              <span className="text-[10px] tracking-wider uppercase text-[#6C7A9C] px-2 py-0.5 rounded border border-white/10">
                {it.level}
              </span>
            )}
            {isUrgent && (
              <span className="text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded bg-[#C83A3A]/15 text-[#C83A3A] border border-[#C83A3A]/40 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {it.urgent}
              </span>
            )}
          </div>

          <h3 className="mt-2 text-white text-lg font-semibold leading-tight">{it.name}</h3>

          <div className="mt-1.5 flex items-center gap-2 text-[#A0AABF] text-sm">
            <MapPin className="w-3.5 h-3.5" />
            <span>{it.county}{it.city && it.city !== it.county ? ` · ${it.city}` : ""}</span>
          </div>

          {it.address && <div className="mt-1 text-[#A0AABF] text-sm">{it.address}</div>}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {phones.map((p, i) => (
              <a
                key={`${it.inst_id}-p-${i}`}
                href={`tel:${p.replace(/[^0-9+]/g, "")}`}
                data-testid={`inst-phone-${it.inst_id}-${i}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border border-white/10 text-[#F8F8F4]/90 hover:border-[#D4A63D] hover:text-[#D4A63D] transition"
                title="Sună"
              >
                <Phone className="w-3.5 h-3.5" /> {p}
              </a>
            ))}
            {it.email && (
              <a
                href={`mailto:${it.email}`}
                data-testid={`inst-email-${it.inst_id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border border-white/10 text-[#F8F8F4]/90 hover:border-[#5FA0E8] hover:text-[#5FA0E8] transition"
              >
                <Mail className="w-3.5 h-3.5" /> {it.email}
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); copy(it.email, `em-${it.inst_id}`); }}
                  className="ml-1 opacity-60 hover:opacity-100"
                  title="Copiază"
                >
                  {copied === `em-${it.inst_id}` ? <Check className="w-3 h-3 text-[#3BA76D]" /> : <Copy className="w-3 h-3" />}
                </button>
              </a>
            )}
            {it.website && (
              <a
                href={it.website.split(/\s+/)[0]}
                target="_blank"
                rel="noreferrer"
                data-testid={`inst-web-${it.inst_id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border border-white/10 text-[#F8F8F4]/90 hover:border-[#E89BB8] hover:text-[#E89BB8] transition"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Site oficial
              </a>
            )}
          </div>

          {(it.competency || it.recommendation || it.jurisdiction_note) && (
            <button
              data-testid={`inst-toggle-${it.inst_id}`}
              onClick={() => setOpen(o => !o)}
              className="mt-3 inline-flex items-center gap-1 text-xs text-[#A0AABF] hover:text-[#D4A63D]"
            >
              <Info className="w-3.5 h-3.5" />
              {open ? "Ascunde detalii" : "Vezi competența și recomandarea"}
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          {onCorrect && (
            <button
              data-testid={`inst-correct-${it.inst_id}`}
              onClick={onCorrect}
              className="mt-2 ml-3 inline-flex items-center gap-1 text-[11px] text-[#6C7A9C] hover:text-[#E89BB8]"
              title="Semnalează o corecție (număr greșit, e-mail inactiv etc.)"
            >
              <AlertTriangle className="w-3 h-3" /> Semnalează corecție
            </button>
          )}

          {/* Share row */}
          <div
            data-testid={`inst-share-row-${it.inst_id}`}
            className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-1.5"
          >
            <span className="text-[10px] tracking-widest uppercase text-[#6C7A9C] mr-1 font-bold inline-flex items-center gap-1">
              <Share2 className="w-3 h-3" /> Trimite
            </span>
            <button
              data-testid={`inst-share-wa-${it.inst_id}`}
              onClick={shareViaWhatsApp}
              title="Trimite pe WhatsApp"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold tracking-wider border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"
            >
              <MessageCircle className="w-3 h-3" /> WHATSAPP
            </button>
            <button
              data-testid={`inst-share-sms-${it.inst_id}`}
              onClick={shareViaSMS}
              title="Trimite prin SMS"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold tracking-wider border border-[#5FA0E8]/40 text-[#5FA0E8] hover:bg-[#5FA0E8]/10"
            >
              <Smartphone className="w-3 h-3" /> SMS
            </button>
            <button
              data-testid={`inst-share-copy-${it.inst_id}`}
              onClick={shareViaCopy}
              title="Copiază contactul complet"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold tracking-wider border transition ${
                copied === "share"
                  ? "border-[#3BA76D] text-[#3BA76D] bg-[#3BA76D]/10"
                  : "border-[#D4A63D]/40 text-[#D4A63D] hover:bg-[#D4A63D]/10"
              }`}
            >
              {copied === "share" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied === "share" ? "COPIAT" : "COPY"}
            </button>
            <button
              data-testid={`inst-share-native-${it.inst_id}`}
              onClick={shareNative}
              title="Mai multe opțiuni"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold tracking-wider border border-white/15 text-[#A0AABF] hover:text-white hover:border-white/30"
            >
              <Share2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/5 px-5 py-4 bg-black/20 space-y-3 text-sm">
          {it.competency && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#A0AABF] font-bold mb-1">Competență</div>
              <p className="text-[#F8F8F4]/90 leading-relaxed">{it.competency}</p>
            </div>
          )}
          {it.recommendation && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#D4A63D] font-bold mb-1">Recomandare</div>
              <p className="text-[#F8F8F4]/90 leading-relaxed">{it.recommendation}</p>
            </div>
          )}
          {it.jurisdiction_note && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#9C8FE0] font-bold mb-1">Notă de jurisdicție</div>
              <p className="text-[#F8F8F4]/85 leading-relaxed">{it.jurisdiction_note}</p>
            </div>
          )}
          {it.verification_status && (
            <div className="pt-2 border-t border-white/5 text-[11px] text-[#6C7A9C] flex items-center gap-2">
              <FileText className="w-3 h-3" />
              <span>{it.verification_status}</span>
              {it.verified_at && <span>· verificat {it.verified_at}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Institutii() {
  const [items, setItems] = useState([]);
  const [county, setCounty] = useState("toate");
  const [type, setType] = useState("toate");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState({ counts: {}, sparse_threshold: 6, total: 0 });
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestPrefill, setSuggestPrefill] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/institutions", { params: { county, type, q: q || undefined, limit: 500 } })
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [county, type, q]);

  useEffect(() => {
    api.get("/institutions/coverage").then(r => setCoverage(r.data || {})).catch(() => {});
  }, []);

  const openSuggest = (prefill = null) => { setSuggestPrefill(prefill); setSuggestOpen(true); };

  // Group by county
  const grouped = useMemo(() => {
    const g = {};
    items.forEach(it => {
      const c = it.county || "Alte";
      (g[c] = g[c] || []).push(it);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b, "ro"));
  }, [items]);

  return (
    <div data-testid="page-institutii" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-8">
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Registru național · verificat 2026-08-05</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#F8F8F4]">Instituții pentru protecția copilului</h1>
        <p className="mt-4 text-[#A0AABF] text-lg max-w-3xl">
          <b className="text-white">229 contacte oficiale</b> — DGASPC, poliție, judecătorii, tribunale, parchet, Avocatul Poporului și linii de urgență. Toate cele 42 de județe + București.
        </p>
      </header>

      {/* Emergency banner */}
      <div className="mb-8 rounded-md border border-[#C83A3A]/30 bg-[#C83A3A]/5 p-5 flex gap-4">
        <AlertTriangle className="w-6 h-6 text-[#C83A3A] shrink-0 mt-0.5" />
        <div className="text-sm text-[#F8F8F4]/90 leading-relaxed">
          <b className="text-[#C83A3A]">Pericol imediat pentru copil?</b> Sună <a href="tel:112" className="underline font-bold">112</a>.
          Pentru abuz, neglijare, exploatare sau violență asupra unui copil: <a href="tel:119" className="underline font-bold text-[#D4A63D]">119</a> (24/7, gratuit, confidențial).
          Pentru sprijin și consiliere: <a href="tel:0800801200" className="underline font-bold">0800 801 200</a> (Telefonul Copilului).
        </div>
      </div>

      {/* Interactive map */}
      <div className="mb-8">
        <RomaniaMap selected={county === "toate" ? null : county} onSelect={(c) => setCounty(c || "toate")} />
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-[1fr_auto] gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6C7A9C]" />
          <input
            data-testid="inst-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută după nume, oraș, competență..."
            className="w-full pl-10 pr-4 py-3 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
          />
        </div>
        <div className="text-[#A0AABF] text-sm inline-flex items-center gap-2 px-3">
          <b className="text-white">{items.length}</b> rezultate {county !== "toate" ? `în ${county}` : "la nivel național"}
        </div>
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2 mb-8">
        {TYPE_FILTERS.map(f => {
          const active = type === f.v;
          const meta = TYPE_META[f.v] || {};
          const M = meta.icon;
          return (
            <button
              key={f.v}
              data-testid={`inst-filter-${f.v}`}
              onClick={() => setType(f.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                active
                  ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]"
                  : "border-white/10 text-[#A0AABF] hover:text-white hover:border-white/30"
              }`}
              style={active ? {} : (meta.color ? { borderColor: `${meta.color}40` } : {})}
            >
              {M && <M className="w-3.5 h-3.5" />} {f.l}
            </button>
          );
        })}
        {county !== "toate" && (
          <button
            onClick={() => setCounty("toate")}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E89BB8]/40 text-[#E89BB8] hover:bg-[#E89BB8]/10 inline-flex items-center gap-1.5"
          >
            <MapPin className="w-3.5 h-3.5" /> {county} × Șterge filtrul
          </button>
        )}
      </div>

      {/* Sparse county notice */}
      {county !== "toate" && (coverage.counts?.[county] || 0) < (coverage.sparse_threshold || 6) && (
        <div
          data-testid="sparse-banner"
          className="mb-8 rounded-md border border-[#D4A63D]/30 bg-gradient-to-r from-[#D4A63D]/10 via-[#D4A63D]/5 to-transparent p-5 flex flex-wrap items-center gap-4 justify-between"
        >
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <div className="w-10 h-10 rounded-md grid place-items-center bg-[#D4A63D]/15 border border-[#D4A63D]/40 shrink-0">
              <Sparkles className="w-5 h-5 text-[#D4A63D]" />
            </div>
            <div>
              <div className="text-white font-semibold">
                <b className="text-[#D4A63D]">{county}</b> are doar {coverage.counts?.[county] || 0} contacte în registru.
              </div>
              <div className="text-sm text-[#A0AABF] mt-1">
                Cunoști o instituție locală care ajută părinții și copiii? Ajută alți părinți din județul tău adăugând contactul verificat.
              </div>
            </div>
          </div>
          <button
            data-testid="sparse-cta-add"
            onClick={() => openSuggest({ action: "add", county })}
            className="btn-gold px-5 py-2.5 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2 shrink-0"
          >
            <PlusCircle className="w-4 h-4" /> ADAUGĂ UN CONTACT
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="py-24 text-center text-[#A0AABF]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Se încarcă instituțiile...</div>
      ) : grouped.length === 0 ? (
        <div className="py-24 text-center text-[#6C7A9C]">Niciun rezultat pentru filtrele curente.</div>
      ) : (
        <div className="space-y-10">
          {grouped.map(([c, list]) => (
            <div key={c} data-testid={`inst-group-${c}`}>
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-4 h-4 text-[#D4A63D]" />
                <h2 className="font-display text-2xl md:text-3xl text-white">{c}</h2>
                <span className="text-xs tracking-widest uppercase text-[#6C7A9C]">{list.length} instituți{list.length === 1 ? "e" : "i"}</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {list.map(it => (
                  <InstitutionCard key={it.inst_id} it={it} onCorrect={() => openSuggest({ action: "correct", county: it.county, existing_inst_id: it.inst_id, name: it.name })} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Global CTA */}
      <div className="mt-12 rounded-md border border-white/10 bg-white/[0.02] p-6 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex-1 min-w-[260px]">
          <div className="text-white font-semibold">Ajută-ne să menținem registrul actualizat</div>
          <div className="text-sm text-[#A0AABF] mt-1">
            Ai găsit un contact greșit, un număr care nu mai funcționează sau lipsește o instituție? Trimite-ne o sugestie — verificăm și publicăm.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="global-cta-add"
            onClick={() => openSuggest({ action: "add" })}
            className="btn-gold px-5 py-2.5 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" /> ADAUGĂ INSTITUȚIE
          </button>
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-16 p-5 rounded-md border border-white/10 bg-white/[0.02] text-xs text-[#6C7A9C] leading-relaxed">
        <b className="text-[#A0AABF]">Notă:</b> Datele sunt verificate la data 2026-08-05 din surse oficiale (site-uri instituționale, portalul instanțelor, Ministerul Public). Datele se pot modifica — confirmă programul și competența teritorială înainte de deplasare. Această pagină NU oferă consultanță juridică sau psihologică; folosește-o ca punct de plecare pentru contactul cu instituția potrivită.
      </div>

      {suggestOpen && (
        <SuggestionModal
          onClose={() => setSuggestOpen(false)}
          prefill={suggestPrefill}
        />
      )}
    </div>
  );
}

/* ============ SUGGESTION MODAL ============ */
const KIND_OPTIONS = ["DGASPC", "Poliție", "Judecătorie", "Tribunal", "Parchet", "Avocatul Poporului", "Linie de urgență", "Serviciu social", "Alta"];

const SuggestionModal = ({ onClose, prefill }) => {
  const [form, setForm] = useState({
    action: prefill?.action || "add",
    county: prefill?.county || "",
    kind: prefill?.kind || "",
    name: prefill?.name || "",
    address: "",
    phone: "",
    email: "",
    website: "",
    notes: "",
    submitter_email: "",
    existing_inst_id: prefill?.existing_inst_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.county.trim()) { toast.error("Județul este obligatoriu."); return; }
    if (form.action === "add" && !form.name.trim()) { toast.error("Numele instituției este obligatoriu."); return; }
    if (form.action === "correct" && !form.notes.trim()) { toast.error("Descrie ce trebuie corectat."); return; }
    setSaving(true);
    try {
      await api.post("/institutions/suggest", form);
      setSent(true);
      toast.success("Mulțumim! Sugestia ta a fost trimisă.");
    } catch (e) {
      const msg = e.response?.data?.detail || "Nu am putut trimite sugestia.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="suggest-modal"
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="max-w-lg mx-auto mt-24 mx-4 rounded-lg border border-[#D4A63D]/30 bg-[#0F1830] shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[#D4A63D] font-bold">Registru național</div>
            <h3 className="text-white text-lg font-semibold mt-0.5">
              {form.action === "correct" ? "Semnalează o corecție" : "Adaugă o instituție"}
            </h3>
          </div>
          <button data-testid="suggest-close" onClick={onClose} className="text-[#A0AABF] hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#3BA76D]/15 border border-[#3BA76D]/40 grid place-items-center mx-auto mb-4">
              <Check className="w-7 h-7 text-[#3BA76D]" />
            </div>
            <h4 className="text-white font-semibold text-lg mb-2">Mulțumim pentru contribuție!</h4>
            <p className="text-[#A0AABF] text-sm">Verificăm sugestia și o publicăm în registru dacă este validă. Poți închide fereastra.</p>
            <button
              onClick={onClose}
              className="btn-gold mt-6 px-6 py-2 rounded-sm text-xs font-bold tracking-wider"
            >
              ÎNCHIDE
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {form.action === "correct" && form.name && (
              <div className="p-3 rounded-sm bg-[#E89BB8]/10 border border-[#E89BB8]/30 text-sm text-[#F8F8F4]/90">
                <b className="text-[#E89BB8]">Corecție pentru:</b> {form.name} · {form.county}
              </div>
            )}

            {form.action === "add" && (
              <div>
                <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Județ *</label>
                <input
                  data-testid="suggest-county"
                  value={form.county}
                  onChange={(e) => setForm({ ...form, county: e.target.value })}
                  placeholder="ex: Alba, Cluj, București..."
                  className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                />
              </div>
            )}

            {form.action === "add" && (
              <>
                <div>
                  <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Tip instituție</label>
                  <select
                    data-testid="suggest-kind"
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                  >
                    <option value="">Alege...</option>
                    {KIND_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Denumire *</label>
                  <input
                    data-testid="suggest-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ex: Serviciul de Asistență Socială Sebeș"
                    className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Telefon</label>
                    <input
                      data-testid="suggest-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="0258 xxx xxx"
                      className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">E-mail</label>
                    <input
                      data-testid="suggest-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="contact@..."
                      className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Site web</label>
                  <input
                    data-testid="suggest-website"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">Adresă</label>
                  <input
                    data-testid="suggest-address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Str. ..."
                    className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">
                {form.action === "correct" ? "Ce trebuie corectat? *" : "Note pentru moderator"}
              </label>
              <textarea
                data-testid="suggest-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 2000) })}
                rows={4}
                placeholder={form.action === "correct" ? "Ex: telefonul de la Judecătorie nu mai funcționează, e-mailul returnează bounce, adresa s-a mutat..." : "Ce ai vrea să știm despre această instituție?"}
                className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm resize-none"
              />
            </div>

            <div>
              <label className="block text-white text-xs font-semibold mb-1.5 tracking-wide">E-mail contact (opțional)</label>
              <input
                data-testid="suggest-submitter-email"
                value={form.submitter_email}
                onChange={(e) => setForm({ ...form, submitter_email: e.target.value })}
                placeholder="ca să te putem contacta dacă avem întrebări"
                className="w-full px-3 py-2.5 rounded-sm bg-[#0A1020] border border-white/10 focus:border-[#D4A63D] outline-none text-white text-sm"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-sm text-xs font-semibold tracking-wider border border-white/10 text-[#A0AABF] hover:text-white"
              >
                ANULEAZĂ
              </button>
              <button
                data-testid="suggest-submit"
                disabled={saving}
                onClick={submit}
                className="btn-gold px-5 py-2.5 rounded-sm text-xs font-bold tracking-wider inline-flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                TRIMITE SUGESTIA
              </button>
            </div>

            <p className="text-[10px] text-[#6C7A9C] leading-relaxed pt-2 border-t border-white/5">
              Sugestiile sunt anonime dacă nu completezi e-mailul. Verificăm fiecare propunere înainte de publicare — mulțumim pentru contribuție.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
